import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Logo } from '@cortex/web-design-system/components/ui/Logo';
import { useAuth } from '../hooks/useAuth.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub のログインページ。認証済みユーザーをホームへ送り、Google OAuth サインインを提供する。
 * @graph-connects useAuth [calls] 認証フック
 */
export function LoginPage() {
  const { isAuthenticated, isLoading, login, isLoggingIn } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const oauthError = new URLSearchParams(window.location.search).get('error');

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      void navigate({ to: '/' });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="gradient-orb gradient-orb-primary w-96 h-96 -top-48 -left-48" />
        <div className="gradient-orb gradient-orb-success w-96 h-96 -bottom-48 -right-48" />
      </div>

      <div className="relative w-full max-w-md">
        <Card className="animate-fade-in-up">
          <div className="flex justify-center mb-8">
            <Logo size="lg" />
          </div>

          <h1 className="text-lg sm:text-xl font-bold text-[var(--color-text-primary)] text-center">
            {t('app.name')}
          </h1>
          <p className="mt-2 text-[var(--color-text-secondary)] text-center">
            {t('login.description')}
          </p>

          {oauthError ? (
            <div className="mt-5 rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error-muted)] px-4 py-3 text-sm text-[var(--color-error)]">
              {t('login.oauthError', { error: oauthError })}
            </div>
          ) : null}

          <div className="mt-8">
            <Button
              onClick={() => {
                login();
              }}
              size="lg"
              fullWidth
              loading={isLoggingIn}
              leftIcon={
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              }
            >
              {isLoggingIn ? t('login.redirecting') : t('login.googleLogin')}
            </Button>
          </div>

          <p className="mt-8 text-xs text-[var(--color-text-muted)] text-center">
            {t('login.domainHint')}
          </p>
        </Card>
      </div>
    </div>
  );
}
